/* =========================================================================
 * THE ACTIVITY LENS — ONE CATEGORY, EVERY GOVERNING BODY, NOTHING HIDDEN.
 *
 * Brian: "I like the lens idea and it can also be done crossing every other
 * governing body by sport or activity."
 *
 * So cheer stops being the mechanism and becomes the first preset. This module
 * is the read-time normalisation the lens stands on: it turns the 222 distinct
 * spellings of `sr_sport` measured live in object 2-64425303 on 2026-09-21
 * into canonical ACTIVITIES, so a lens keyed on an activity counts every
 * record in it.
 *
 * ⚠️ IT NORMALISES FOR READING. IT DOES NOT REPAIR THE RECORDS.
 * Nothing in this file writes. Rewriting `sr_sport` on live records is a data
 * repair that belongs to a human — automation proposes, it does not overwrite.
 * `raw` is carried on every result so the stored spelling is never hidden
 * behind the tidy name.
 *
 * ⚠️ AND NOTHING IS EVER DROPPED.
 * Brian: "There cannot be any invisible sports. If it's Cheer let it live in
 * there, flag it and say that it isn't complete... because every championship
 * needs to be pursued."
 *
 * That is the load-bearing rule of this whole build, and it inverts the usual
 * instinct. A record we cannot fully classify must be MORE visible than one we
 * can, not less. So a spelling that matches no rule does NOT fall into a null
 * bucket that the UI then filters away — it becomes its own activity, under
 * its own repaired name. A record with no sport at all becomes
 * ACTIVITY_UNRECORDED, which is a real, selectable, countable activity.
 * Suppressing an unclassified record is how a pursuable title silently stops
 * existing.
 * ========================================================================= */

const S = (v) => String(v ?? '').trim()

/* -------------------------------------------------------------------------
 * 1. MOJIBAKE, REVERSED BY THE ENCODING TABLE — NOT BY A LIST OF STRINGS.
 *
 * ⚠️ THIS FILE USED TO CARRY `.replace(/â€"/g, '—')` AND EIGHT MORE LIKE IT,
 * AND THAT WAS THE WRONG SHAPE OF FIX.
 *
 * A hand-written replace list only repairs the corruptions someone already
 * noticed. The object holds "Cheer — Game Day" four times with a real em dash
 * and four more as "Cheer â€" Game Day"; a list catches that pair and misses
 * the next one silently, and a miss here is invisible because both spellings
 * look like data.
 *
 * The corruption has ONE cause: UTF-8 bytes decoded as cp1252. So reverse the
 * cause. Encode the string back to cp1252 bytes and decode those bytes as
 * UTF-8. Every corruption of that family is undone, including ones nobody has
 * seen yet, and the rule is checkable against the two published encoding
 * tables rather than against someone's memory.
 *
 * TextDecoder is used with `fatal: true` ON PURPOSE. It is the safety
 * interlock: if the bytes are not valid UTF-8, the string was never mojibake
 * and the original is returned untouched. That is what keeps a legitimately
 * accented name — "Championnat", "Español" — from being mangled by a repair it
 * did not need.
 * ----------------------------------------------------------------------- */

/* The cp1252 0x80–0x9F block, the only place cp1252 and latin-1 disagree.
   Codepoint → byte. This is the published table, transcribed, not invented. */
const CP1252_HIGH = new Map([
  [0x20ac, 0x80], [0x201a, 0x82], [0x0192, 0x83], [0x201e, 0x84],
  [0x2026, 0x85], [0x2020, 0x86], [0x2021, 0x87], [0x02c6, 0x88],
  [0x2030, 0x89], [0x0160, 0x8a], [0x2039, 0x8b], [0x0152, 0x8c],
  [0x017d, 0x8e], [0x2018, 0x91], [0x2019, 0x92], [0x201c, 0x93],
  [0x201d, 0x94], [0x2022, 0x95], [0x2013, 0x96], [0x2014, 0x97],
  [0x02dc, 0x98], [0x2122, 0x99], [0x0161, 0x9a], [0x203a, 0x9b],
  [0x0153, 0x9c], [0x017e, 0x9e], [0x0178, 0x9f],
])

/** Encode to cp1252 bytes, or null if any character has no cp1252 byte —
 *  in which case the string cannot be the product of a cp1252 misread. */
function toCp1252(text) {
  const out = new Uint8Array(text.length)
  for (let i = 0; i < text.length; i += 1) {
    const cp = text.codePointAt(i)
    if (cp > 0xffff) return null
    if (cp <= 0x7f || (cp >= 0xa0 && cp <= 0xff)) { out[i] = cp; continue }
    const byte = CP1252_HIGH.get(cp)
    if (byte === undefined) return null
    out[i] = byte
  }
  return out
}

/* fatal:true is the interlock described above. One decoder, reused. */
const UTF8 = typeof TextDecoder === 'undefined'
  ? null
  : new TextDecoder('utf-8', { fatal: true })

/**
 * Undo cp1252-misread-UTF-8, as many times as it was applied.
 *
 * ⚠️ IT LOOPS BECAUSE DOUBLE ENCODING HAPPENS. A string that went through the
 * fault twice needs the round trip twice; a bounded loop costs nothing and a
 * single pass would leave "Ã¢â‚¬â€" half repaired, which is worse than not
 * repairing it at all because it looks fixed.
 */
export function repairMojibake(value) {
  let s = S(value)
  if (!s || !UTF8) return s
  for (let pass = 0; pass < 4; pass += 1) {
    const bytes = toCp1252(s)
    if (!bytes) return s
    let decoded
    try { decoded = UTF8.decode(bytes) } catch { return s }
    if (decoded === s) return s
    s = decoded
  }
  return s
}

/** True when the stored spelling was corrupted — used to SIZE the defect on
 *  screen, never to hide the row. */
export const isCorrupted = (value) => repairMojibake(value) !== S(value)

/* -------------------------------------------------------------------------
 * 2. THE CANONICAL ACTIVITY.
 *
 * ⚠️ SHORT TOKENS GET WORD BOUNDARIES, AND THIS IS NOT PEDANTRY. It is the
 * fourth incident of the same class in this codebase: "pom" matched inside
 * "Pommel Horse" and filed a gymnastics title as cheer; "UDA" matched inside
 * "Newport BermUDA Race" and proposed moving a SAILING RACE onto the Varsity
 * Spirit card; "NC" inside "NCHSAA"; "AAA" inside "Class AAA".
 *
 * ⚠️ AND ORDER IS LOAD-BEARING, FOR THE SAME REASON.
 * "Asphalt Short Track" (35 records) and "Dirt Late Model" are MOTORSPORT.
 * A naive /track/ rule folds all 35 into Track & Field, which would inflate
 * the biggest activity in the object with cars — a wrong number that looks
 * completely plausible. Motorsport is therefore tested BEFORE track, and the
 * test below pins that.
 * ----------------------------------------------------------------------- */

export const ACTIVITY_UNRECORDED = 'Activity not recorded'

const MOTORSPORT = /\b(motorsports?|nascar|indycar|karting|motocross|supercross|sprint car|late model|dirt modified|midget|open wheel|drag racing|auto racing|powerboat|asphalt|endurance|superbike|rallycross)\b/i

/* Cheer and dance keeps the two-tier token rule that was measured into it:
   "dance", "drill" and "pom" name the sport alone; "jazz", "hip hop", "kick",
   "lyrical" and "show" do NOT — they are also music and theatre words, and
   treating them as strong signals pulled seven OSSAA "State Jazz Band"
   titles, which are high-school BAND championships, into cheer. */
const HAS_CHEER = /\bcheer(leading|ing|leaders?)?\b/i
const HAS_STUNT = /\bstunt\b/i
const HAS_SPIRIT = /\bspirit\b/i
const STRONG_DANCE = /\b(dance|drill|poms?)\b/i
const SOFT_DANCE = /\b(jazz|hip[\s-]?hop|kick|lyrical|show)\b/i

/**
 * Ordered rules. First match wins, so anything with a substring trap sits
 * above the rule it would fool. Everything that falls through keeps its own
 * repaired spelling as its activity — see the no-invisible-sports rule above.
 */
const RULES = [
  ['Motorsports', MOTORSPORT],
  ['Horse Racing', /\bhorse racing\b/i],
  ['Cheer and Dance', (s) => {
    if (HAS_CHEER.test(s) || HAS_STUNT.test(s)) return true
    if (STRONG_DANCE.test(s)) return true
    if (HAS_SPIRIT.test(s)) return true
    return false
  }],
  ['Cross Country', /\bcross[\s-]?country\b/i],
  ['Track & Field', /\btrack\b/i],
  ['Marching Band & Music', /\b(marching|band|choir|orchestra|music|drumline|colou?r guard)\b/i],
  ['Speech & Debate', /\b(speech|debate|forensics|oratory|congress)\b/i],
  ['Theatre & Drama', /\b(theatre|theater|drama|thespian|one[\s-]?act|play|film)\b/i],
  ['Academics', /\b(academic|scholars?|scholastic bowl|quiz|literary|journalism|creative writing|chess)\b/i],
  ['Esports', /\besports?\b/i],
  ['Robotics', /\brobot/i],
  ['Rodeo', /\brodeo\b/i],
  ['Fishing', /\bfishing\b/i],
  ['Wrestling', /\bwrestl/i],
  ['Flag Football', /\bflag football\b/i],
  ['Football', /\bfootball\b/i],
  ['Basketball', /\bbasketball\b/i],
  ['Soccer', /\b(soccer|futsal)\b/i],
  ['Baseball', /\bbaseball\b/i],
  ['Softball', /\b(softball|slo-?pitch|slowpitch)\b/i],
  ['Beach Volleyball', /\bbeach volleyball\b/i],
  ['Volleyball', /\bvolleyball\b/i],
  ['Ice Hockey', /\b(ice hockey|ringette)\b/i],
  ['Field Hockey', /\bfield hockey\b/i],
  ['Hockey', /\bhockey\b/i],
  ['Lacrosse', /\blacrosse\b/i],
  ['Tennis', /\btennis\b/i],
  ['Golf', /\bgolf\b/i],
  ['Swimming & Diving', /\b(swimming|diving|artistic swimming)\b/i],
  ['Water Polo', /\bwater polo\b/i],
  ['Rowing', /\b(rowing|crew)\b/i],
  ['Sailing', /\bsailing\b/i],
  ['Gymnastics', /\b(gymnastics|pommel|tumbling|acrobatics)\b/i],
  ['Bowling', /\bbowling\b/i],
  ['Equestrian', /\b(equestrian|riding)\b/i],
  ['Archery', /\barchery\b/i],
  ['Shooting Sports', /\b(clay target|riflery|rifle|shooting sports|trap|skeet)\b/i],
  ['Rugby', /\brugby\b/i],
  ['Badminton', /\bbadminton\b/i],
  ['Curling', /\bcurling\b/i],
  ['Snow Sports', /\b(ski|skiing|snowboard|snowboarding)\b/i],
  ['Figure Skating', /\bfigure skating\b/i],
  ['Strength Sports', /\b(powerlifting|weightlifting|bodybuilding|strongman|fitness)\b/i],
  ['Combat Sports', /\b(judo|jiu[\s-]?jitsu|boxing|karate|taekwondo|fencing)\b/i],
  ['Cycling', /\b(cycling|mountain biking|bmx)\b/i],
  ['Triathlon', /\b(triathlon|running)\b/i],
  ['Cricket', /\bcricket\b/i],
  ['Handball', /\bhandball\b/i],
  ['Squash', /\bsquash|racquetball\b/i],
  ['Table Tennis', /\btable tennis\b/i],
  ['Pickleball', /\b(pickleball|paddle)\b/i],
  ['Ultimate', /\bultimate\b/i],
  ['Cornhole', /\b(cornhole|axe throwing|darts|disc golf|double dutch)\b/i],
  ['Action Sports', /\b(action sports|surfing|skateboard)\b/i],
  ['Multi-Sport', /\b(multi|adaptive multi-sport|jrotc)\b/i],
]

/**
 * The canonical activity for one raw spelling.
 * Never returns null: an unmatched spelling is its own activity, and a blank
 * one is ACTIVITY_UNRECORDED. No record is classified out of existence.
 */
export function activityFor(rawSport) {
  const s = repairMojibake(rawSport)
  if (!s) return ACTIVITY_UNRECORDED
  for (const [name, test] of RULES) {
    if (typeof test === 'function' ? test(s) : test.test(s)) return name
  }
  return s
}

/* The second axis: the division or format the spelling names, kept SEPARATE
   from the activity because "Game Day" exists in both cheer and dance and
   folding it into the activity would lose it. */
const DISCIPLINES = [
  ['Game Day', /\bgame[\s-]?day\b/i],
  ['Traditional', /\btraditional\b/i],
  ['All-Girl', /\ball[\s-]?girl\b/i],
  ['Coed', /\bco-?ed\b/i],
  ['Pom', /\bpoms?\b/i],
  ['Jazz', /\bjazz\b/i],
  ['Hip Hop', /\bhip[\s-]?hop\b/i],
  ['STUNT', /\bstunt\b/i],
  ['Competitive', /\bcompetitive\b/i],
  ['Unified', /\bunified\b/i],
  ['Indoor', /\bindoor\b/i],
  ['Outdoor', /\boutdoor\b/i],
  ['Bass', /\bbass\b/i],
  ['Offshore', /\boffshore\b/i],
  ['Alpine', /\balpine\b/i],
  ['Nordic', /\bnordic\b/i],
  ['Winter', /\bwinter\b/i],
  ['Fall', /\bfall\b/i],
  ['Spring', /\bspring\b/i],
]

export function disciplineFor(rawSport) {
  const s = repairMojibake(rawSport)
  if (!s) return ''
  for (const [label, re] of DISCIPLINES) if (re.test(s)) return label
  return ''
}

/** Everything the lens needs about one record's activity, in one pass. */
export function activityOf(record) {
  const r = record?.raw || record || {}
  const raw = S(r.sr_sport ?? record?.sport)
  return {
    raw,
    clean: repairMojibake(raw),
    activity: activityFor(raw),
    discipline: disciplineFor(raw),
    corrupted: isCorrupted(raw),
  }
}

/* -------------------------------------------------------------------------
 * 3. THE PRESETS.
 *
 * Presets are a shortcut to an activity, never the definition of one. The
 * activity list the lens offers is DERIVED from the records in hand, so a
 * harvest that adds a sport shows up with no edit here; these eight are just
 * pinned to the front because they are the ones asked for.
 *
 * ⚠️ "BASS FISHING" IS PINNED, BUT THE ACTIVITY IS FISHING, AND THE LABEL
 * SAYS SO. The object holds 47 records spelled "Bass Fishing", 64 spelled
 * plain "Fishing" and 6 "Offshore Fishing". A preset that showed only the 47
 * would be the exact undercount this module exists to prevent, so the chip
 * opens all 117 and the Bass discipline filter narrows to the 47.
 * ----------------------------------------------------------------------- */
export const ACTIVITY_PRESETS = [
  { key: 'cheer', activity: 'Cheer and Dance', label: 'Cheer and Dance' },
  { key: 'wrestling', activity: 'Wrestling', label: 'Wrestling' },
  { key: 'track', activity: 'Track & Field', label: 'Track & Field' },
  { key: 'esports', activity: 'Esports', label: 'Esports' },
  { key: 'fishing', activity: 'Fishing', label: 'Bass Fishing', note: 'all fishing titles; narrow to Bass with the discipline filter' },
  { key: 'music', activity: 'Marching Band & Music', label: 'Marching Band / Music' },
  { key: 'rodeo', activity: 'Rodeo', label: 'Rodeo' },
  { key: 'robotics', activity: 'Robotics', label: 'Robotics' },
]

/** Back-compat for the cheer wiring that shipped first. */
export const isCheerOrDance = (record) => activityOf(record).activity === 'Cheer and Dance'

/* -------------------------------------------------------------------------
 * 4. COMPLETENESS — WHAT A RECORD IS MISSING, SAID OUT LOUD.
 *
 * ⚠️ THESE FLAGS ARE THE POINT OF THE VIEW, NOT A FOOTNOTE ON IT.
 * Brian: "every championship needs to be pursued". A record whose producer we
 * have not established is a WORK ITEM, so it renders in the lens with the gap
 * named, rather than being routed to a drawer nobody opens. `Unspecified` is
 * treated as missing, not as an answer, because that is what it means.
 * ----------------------------------------------------------------------- */
export const NO_BODY = 'Unspecified'

/* ⚠️ A FLAG THAT FIRES ON EVERY ROW IS NOT A FLAG, IT IS A BACKGROUND COLOUR.
 *
 * The first cut of this function flagged 473 of 473 wrestling records and
 * 1,185 of 1,185 track titles, because it asked "is there a champion?" of
 * championships that HAVE NOT HAPPENED YET. 12,425 records in the object sit
 * at sr_champion_status = 'Scheduled'; a scheduled title with no champion is
 * not a defect, it is next season. Flagging it buries the 2,534 records whose
 * status is genuinely blank underneath twelve thousand rows of noise, and a
 * rep who learns the flag means nothing stops reading it — which is the same
 * outcome as hiding the record, reached the long way round.
 *
 * So each gap below fires only where the field is genuinely OWED:
 *   · a body and an activity are owed by every record, always;
 *   · a champion is owed once the event is no longer pending;
 *   · a state is owed by a state or district title, and by nothing else — a
 *     national or open championship has no state, and saying it is missing one
 *     would be inventing a defect.
 */
const PENDING_CHAMPION = /^(scheduled|in progress|upcoming|pending)$/i
const STATE_SCOPED = /^(state|district|regional|section|provincial)/i

export function gapsFor(record) {
  const r = record?.raw || record || {}
  const gaps = []

  const body = S(r.sr_conference ?? record?.sr_conference)
  if (!body || body === NO_BODY) gaps.push('governing body not established')

  if (!S(r.sr_sport ?? record?.sport)) gaps.push('no activity recorded')

  if (!S(r.sr_event_date ?? record?.sr_event_date) && !record?.effDate) gaps.push('no date')

  const champion = S(record?.champion) || S(r.current_champion)
  const status = S(r.sr_champion_status ?? record?.sr_champion_status)
  if (!champion && !PENDING_CHAMPION.test(status)) {
    gaps.push(status ? 'champion not recorded' : 'champion status not set')
  }

  const level = S(r.sr_level ?? record?.sr_level)
  const state = S(r.sr_state_location ?? record?.sr_state_location) || S(r.sr_state)
  if (!state && STATE_SCOPED.test(level)) gaps.push('no state')

  return gaps
}

export const isIncomplete = (record) => gapsFor(record).length > 0

/* -------------------------------------------------------------------------
 * 5. THE RECONCILIATION.
 *
 * ⚠️ MEMBERSHIP IS NOT A COUNT. If the flagged set plus the clean set does not
 * equal the activity total, the view is dropping records and telling you a
 * tidy number while it does — which is the failure nobody catches, because a
 * smaller number from a narrower view looks like the view working.
 * `reconciles` is asserted in activities.test.js for every preset.
 * ----------------------------------------------------------------------- */
export function reconcile(universe, activity) {
  const inActivity = universe.filter((r) => activityOf(r).activity === activity)
  const incomplete = inActivity.filter(isIncomplete)
  const complete = inActivity.filter((r) => !isIncomplete(r))
  return {
    activity,
    total: inActivity.length,
    incomplete: incomplete.length,
    complete: complete.length,
    reconciles: incomplete.length + complete.length === inActivity.length,
    records: inActivity,
  }
}

/** Every activity present in the universe, largest first. Derived, never typed. */
export function activityIndex(universe = []) {
  const counts = new Map()
  const flagged = new Map()
  for (const rec of universe) {
    const a = activityOf(rec).activity
    counts.set(a, (counts.get(a) || 0) + 1)
    if (isIncomplete(rec)) flagged.set(a, (flagged.get(a) || 0) + 1)
  }
  return [...counts]
    .map(([activity, total]) => ({ activity, total, incomplete: flagged.get(activity) || 0 }))
    .sort((a, b) => b.total - a.total)
}

/**
 * The fragmentation itself, measured rather than asserted, so the figure on
 * the board and the figure in the repair proposal come from one place and
 * cannot drift apart.
 */
export function fragmentation(universe = [], activity = null) {
  const raw = new Map()
  const clean = new Map()
  let corrupted = 0
  for (const rec of universe) {
    const a = activityOf(rec)
    if (activity && a.activity !== activity) continue
    if (!a.raw) continue
    raw.set(a.raw, (raw.get(a.raw) || 0) + 1)
    clean.set(a.clean, (clean.get(a.clean) || 0) + 1)
    if (a.corrupted) corrupted += 1
  }
  return {
    spellings: raw.size,
    spellingsAfterRepair: clean.size,
    corruptedRecords: corrupted,
    duplicatedByEncoding: raw.size - clean.size,
    bySpelling: [...raw].sort((a, b) => b[1] - a[1]),
  }
}
